import { useEffect, useRef, useState } from "react";
import { queryTokenExConfig } from "../services/dataQueries";
import { wvusLogger } from "../helpers";

/**
 * TokenEx authentication keys expire 20 minutes after they are issued. The iframe config
 * is signed server-side with a timestamp, so a page that has been open past that window
 * hands the iframe a key TokenEx rejects. This hook keeps the key fresh like in events-checkout
 */

/** How long TokenEx honours an authentication key, measured from when it was issued. */
export const AUTH_KEY_TTL_MS = 20 * 60 * 1000;
/** Refresh this far into the key's life, leaving a 2 minute buffer before expiry. */
export const PROACTIVE_REFRESH_MS = 18 * 60 * 1000;
export const REFRESH_RETRY_MS = 60 * 1000;
export const REFRESH_RETRY_MAX_MS = 5 * 60 * 1000;
export const MAX_CONSECUTIVE_FAILURES = 6;

type LoadReason = "initial" | "manual" | "proactive" | "visibility";

export function useTokenConfig() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  /** True once background retries have stopped with no usable config in hand. */
  const [exhausted, setExhausted] = useState(false);

  const dataRef = useRef(null);
  const configuredAtRef = useRef(0);
  const refreshTimerRef = useRef(null);
  /** Holds the in-flight request so concurrent callers can wait same outcome. */
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const mountedRef = useRef(true);
  const loadRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleProactiveRefresh = (delay = PROACTIVE_REFRESH_MS) => {
    clearRefreshTimer();
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      loadRef.current?.("proactive")?.catch(() => {});
    }, delay);
  };

  const runLoad = async (reason: LoadReason) => {
    if (reason !== "proactive") {
      setStatus("loading");
    }

    try {
      const results = await queryTokenExConfig();

      if (!mountedRef.current) {
        return false;
      }

      dataRef.current = results;
      configuredAtRef.current = Date.now();
      consecutiveFailuresRef.current = 0;
      setExhausted(false);
      setData(results);
      setStatus("success");
      scheduleProactiveRefresh();

      return true;
    } catch (error) {
      if (!mountedRef.current) {
        return false;
      }

      // Keep serving an existing config rather than blanking the card field since an expired
      // key still lets the donor type, and the reactive path recovers on submit.
      setStatus(dataRef.current ? "success" : "error");

      consecutiveFailuresRef.current += 1;

      // Give up on background retries eventually after attempts
      // Becoming visible still retries and resets this budget
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        wvusLogger.log(
          `TokenEx config unavailable after ${consecutiveFailuresRef.current} attempts, stopping background retries`,
          wvusLogger.getLogLevelNames().ERROR
        );
        // Only when there is nothing to fall back on. Giving up while still holding a
        // working key is not a donor-visible failure.
        setExhausted(!dataRef.current);

        return false;
      }

      wvusLogger.log(
        `TokenEx config ${reason} load failed (attempt ${consecutiveFailuresRef.current}): ${error}`,
        wvusLogger.getLogLevelNames().WARNING
      );

      const backoff = Math.min(
        REFRESH_RETRY_MS * 2 ** (consecutiveFailuresRef.current - 1),
        REFRESH_RETRY_MAX_MS
      );
      scheduleProactiveRefresh(backoff);

      return false;
    }
  };

  const loadConfigs = (reason: LoadReason = "initial"): Promise<boolean> => {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const request = runLoad(reason).finally(() => {
      if (inFlightRef.current === request) {
        inFlightRef.current = null;
      }
    });

    inFlightRef.current = request;

    return request;
  };

  useEffect(() => {
    loadRef.current = loadConfigs;
  });

  const refresh = () => loadConfigs("manual");

  /** Whether the config should be replaced before the donor touches the form again. */
  const needsRefresh = () => {
    if (!dataRef.current) {
      return true;
    }

    return Date.now() - configuredAtRef.current >= PROACTIVE_REFRESH_MS;
  };

  const loadAndForget = (reason: LoadReason) => {
    loadConfigs(reason).catch(() => {});
  };

  useEffect(() => {
    mountedRef.current = true;
    loadAndForget("initial");

    const handleVisibilityChange = () => {
      if (!document.hidden && needsRefresh()) {
        wvusLogger.log(
          "TokenEx config stale on page becoming visible, refreshing",
          wvusLogger.getLogLevelNames().INFO
        );
        // Someone is back on the page, so restore the retry budget
        consecutiveFailuresRef.current = 0;
        loadAndForget("visibility");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearRefreshTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return {
    data,
    status,
    exhausted,
    configuredAt: configuredAtRef.current,
    refresh
  };
}
